import { useEffect, lazy, Suspense } from "react";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactGA from "react-ga4";
import { AnimatePresence } from "framer-motion";
import { jwtDecode } from "jwt-decode";
import { HttpStatusCode } from "axios";

import Loader from "./components/Loader";
import ToastModel from "./components/ToastModel";
import PopupActionModel from "./components/PopupActionModel";
import LoginSignupModal from "./components/LoginSignupModal";
import { PublicRoute } from "./components/PublicRoute";
import { PrivateRoute } from "./components/PrivateRoute";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import AndroidRouteGuard from "./components/AndroidRouteGuard";

import LandingLogin from "./Pages/LandingPage/LandingPage";
import ErrorPage from "./Pages/ErrorPage";

import { Login, Logout } from "./redux/authReducer";
import { CloseToastModal, ToggleSessionTimeoutAlert } from "./redux/uiReducer";
import { loginService } from "./WebApiServices/login.service";

import {
  AddLocalStorageData,
  GetLocalStorageData,
  GetLoggedInUserId,
  RemoveLocalStorageData,
} from "./Helpers/Utilities";
import { PAGE_ROUTES, PROD_DOMAIN, USER } from "./Helpers/Enums";
import { config } from "./Helpers/config";

// Lazy-loaded page components — each becomes its own chunk
const CourseHomePage = lazy(
  () => import("./Pages/Course/HomePage/CourseHomePage"),
);
const CourseMainPage = lazy(
  () => import("./Pages/Course/MainPage/CourseMainPage"),
);
const ClientDashboard = lazy(
  () => import("./Pages/ClientDashboard/ClientDashboard"),
);
const AllCourses = lazy(() => import("./Pages/LandingPage/AllCourses"));
const Unsubscribe = lazy(() => import("./Pages/Unsubscribe"));
const ProfilePage = lazy(() => import("./Pages/User/ProfilePage"));
const TrainingLogBook = lazy(() => import("./Pages/TrainingLogBook"));
const UserLeaderboard = lazy(
  () => import("./Pages/ClientDashboard/UserLeaderboard"),
);
const TrainingLog = lazy(() => import("./Pages/TrainingLog"));
const CourseConsolidation = lazy(
  () => import("./Pages/CourseConsolidation/CourseConsolidation"),
);
const Landing = lazy(() => import("./landing/Landing"));
const TermsConditions = lazy(() => import("./Pages/TermsConditions"));
const ContactUs = lazy(() => import("./Pages/ContactUs"));

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    isLoading,
    showToast,
    showActionModal,
    actionModalTitle,
    actionModalText,
    showLoginSignupModal,
  } = useSelector((state) => state.ui);

  let locationData = location.pathname;

  useEffect(() => {
    const previewLink = GetLocalStorageData("PreviewPageFromAdmin");

    const fetchAndAuthenticate = async () => {
      try {
        const res = await loginService.GetToken();
        console.log("Token:", res);

        const token = res.data.token;

        if (!token) return;

        const payload = jwtDecode(token);

        console.log("Payload: ", payload);

        // 🧩 Authenticate using the new Admin-provided credentials
        if (payload.Email && payload.Password) {
          const response = await loginService.AuthenticateUser({
            email: payload.Email,
            password: payload.Password,
          });

          console.log(response);

          if (response.status === HttpStatusCode.Ok) {
            const { isOnline, authToken, userDetails } = response.data;

            AddLocalStorageData("isOnline", isOnline);

            switch (userDetails.userStatus) {
              case USER.STATUS.ACTIVE: {
                AddLocalStorageData("eUser", userDetails);
                AddLocalStorageData("authToken", authToken);
                dispatch(Login());
                break;
              }

              case USER.STATUS.INACTIVE: {
                throw new Error(USER.MSSGS.INACTIVE);
              }

              case USER.STATUS.PENDING: {
                throw new Error(USER.MSSGS.PENDING);
              }

              default:
                throw new Error();
            }

            console.log(previewLink);

            if (
              locationData.includes("/Preview/") &&
              window.location.pathname !== previewLink
            )
              navigate(previewLink, {
                replace: true,
              });

            AddLocalStorageData("PreviewPageFromAdmin", null);
          }
        }
      } catch (error) {
        console.error("Error in auto login:", error);
      }
    };

    if (previewLink?.includes("/Preview/")) {
      fetchAndAuthenticate();
    }
  }, []);

  // useEffect(() => {
  //   const isAndroid = /Android/i.test(navigator.userAgent);

  //   if (isAndroid) {
  //     // You can either redirect to a blocked route:
  //     // navigate("/blocked"); // Make sure this route exists

  //     // Or directly block rendering
  //     //alert("This site is not accessible on Android devices.");
  //     document.body.innerHTML =
  //       "<h2 style='text-align:center;'>This website is currently not supported on Android devices.</h2>";
  //   }
  // }, []);

  /* <summary>
  date: 12-07-2023
  Name: VK
  description: Google Analytics
  <summary>*/
  useEffect(() => {
    // Check if the current domain matches the allowed domain
    if (window.location.hostname === PROD_DOMAIN) {
      ReactGA.initialize(config.gaTrackingId);
      ReactGA.send({ hitType: "pageview", page: window.location.pathname });
    }
  }, []);

  // PL 02-09-2023 - Removed dispatch from dependency array to fix login button being shown even if user localStorage data is present
  useEffect(() => {
    const user = GetLoggedInUserId();
    if (user) {
      dispatch(Login());
    }
    document.addEventListener("contextmenu", (e) => {
      e.preventDefault();
    });

    return () => {
      document.removeEventListener("contextmenu", () => {});
    };
  }, []);

  /* <summary>
  date: 24-02-2023
  Name: GM
  description: Clear user details and reload
  <summary>*/
  const ClosePopupActionModal = () => {
    loginService.Logout();
    RemoveLocalStorageData("eUser");

    dispatch(CloseToastModal());
    dispatch(ToggleSessionTimeoutAlert(false));
    dispatch(Logout());

    navigate("/");
    window.location.reload();
  };

  return (
    <>
      <ScrollToTop />

      {isLoading && <Loader />}

      {showActionModal && (
        <PopupActionModel
          title={actionModalTitle}
          message={actionModalText}
          onClose={ClosePopupActionModal}
        />
      )}

      {!showActionModal && showToast && <ToastModel />}

      <AnimatePresence>
        {showLoginSignupModal && <LoginSignupModal />}
      </AnimatePresence>

      <AndroidRouteGuard>
        <Suspense fallback={<Loader />}>
          <Routes location={location} key={location.pathname}>
            {/* PUBLIC ROUTES */}
            <Route path={PAGE_ROUTES.PUBLIC_ROUTE} element={<PublicRoute />}>
              <Route
                path={PAGE_ROUTES.LOGIN}
                element={
                  <LandingLogin navigate={navigate} location={location} />
                }
              />

              <Route path={PAGE_ROUTES.PUBLIC_ROUTE} element={<Landing />} />

              <Route
                path={PAGE_ROUTES.TERMSANDCONDITIONS}
                element={
                  <TermsConditions navigate={navigate} location={location} />
                }
              />

              <Route path={PAGE_ROUTES.ERRORPAGE} element={<ErrorPage />} />

              <Route
                path={PAGE_ROUTES.COURSE_CONSOLIDATION}
                element={<CourseConsolidation />}
              />

              <Route
                path={PAGE_ROUTES.COURSE_PREVIEW_PAGE}
                element={
                  <CourseMainPage
                    navigate={navigate}
                    location={location}
                    isPreview={true}
                  />
                }
              />
            </Route>

            {/* PRIVATE ROUTES */}
            <Route path="/" element={<PrivateRoute />}>
              <Route
                path={PAGE_ROUTES.COURSE_MAIN_PAGE}
                element={
                  <CourseMainPage navigate={navigate} location={location} />
                }
              />

              <Route
                path={PAGE_ROUTES.COURSES}
                element={<AllCourses navigate={navigate} location={location} />}
              />

              <Route
                path={PAGE_ROUTES.TRAINING_LOG_BOOK}
                element={
                  <ProtectedRoute>
                    <TrainingLogBook navigate={navigate} location={location} />
                  </ProtectedRoute>
                }
              />

              <Route
                path={PAGE_ROUTES.TRAINING_LOG}
                element={
                  <ProtectedRoute>
                    <TrainingLog navigate={navigate} location={location} />
                  </ProtectedRoute>
                }
              />

              {/* UNSUBSCRIBE */}
              <Route path={PAGE_ROUTES.UNSUBSCRIBE} element={<Unsubscribe />} />

              <Route
                path={PAGE_ROUTES.HOME}
                element={
                  <ClientDashboard navigate={navigate} location={location} />
                }
              />

              <Route
                path={PAGE_ROUTES.LEADERBOARD}
                element={
                  <UserLeaderboard navigate={navigate} location={location} />
                }
              />

              <Route
                path={`${PAGE_ROUTES.PROFILE_PAGE}`}
                element={<ProfilePage />}
              />

              <Route
                path={PAGE_ROUTES.COURSE_HOME_PAGE}
                element={
                  <CourseHomePage navigate={navigate} location={location} />
                }
              />

              <Route
                path={PAGE_ROUTES.CONTACT}
                element={<ContactUs navigate={navigate} location={location} />}
              />
            </Route>

            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </Suspense>
      </AndroidRouteGuard>
    </>
  );
};

export default App;
